import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import ErrorWithCountdown from '#/shared/components/ErrorWithCountdown';
import TripToolTabs from '#/shared/components/TripToolTabs';
import DetailNavHeader from '#/shared/components/DetailNavHeader';
import StopsList from '#/transit-tracker/components/StopsList';
import BrandFooter from '#/transit-tracker/components/BrandFooter';
import CurrentLocationPrompt from '#/transit-tracker/components/CurrentLocationPrompt';
import useNearbyOrFavoriteArrivals from '#/transit-tracker/hooks/useNearbyOrFavoriteArrivals';
import useFavoriteStops from '#/transit-tracker/hooks/useFavoriteStops';
import useHighlightedStop from '#/transit-tracker/hooks/useHighlightedStop';
import useGeolocation from '#/shared/hooks/useGeolocation';
import useCurrentLocation from '#/shared/hooks/useCurrentLocation';
import useViewMapControls from '#/map/hooks/useViewMapControls';
import useNavigate from '#/shared/hooks/useNavigate';
import useCurrentQuery from '#/trip-planner/hooks/useCurrentQuery';
import useSidePanelToggle from '#/user/hooks/useSidePanelToggle';
import useInterval from '#/shared/hooks/useInterval';
import OmniboxButton from '#/shared/components/OmniboxButton';
import { setZoomToLocation } from '#/map/slice';
import ROUTES from '#/constants/routes';
import HistoryManager from '#/shared/components/HistoryManager';
import {
  ARRIVALS_REFRESH_INTERVAL,
  GEOLOCATION_REPOLL_INTERVAL,
} from '#/shared/constants';
import GTM_HANDLES from '#/shared/gtm-handles';
import { getDefaultQuery } from '#/trip-planner/utils/current-query';
import SafariFocusHolderInput from '#/shared/components/Styled/SafariFocusHolderInput';
import * as Styled from './styled';

function TripToolsContainer() {
  const [seekingGeoUpdate, setSeekingGeoUpdate] = useState(false);

  const alertBannerIsVisible = useSelector(s => s.user.alertBannerIsVisible);
  const { navigate, navigateToStopAndSign, matchPath } = useNavigate();

  const { toggleFavoriteStop, isFavoriteRouteStop, favoriteStops } =
    useFavoriteStops();
  const { toggleSidePanelMinMax, sidePanelIsMinimized, readyToRemoveFromDom } =
    useSidePanelToggle();
  const { setHighlightedStop } = useHighlightedStop();
  const intl = useIntl();
  const {
    favoriteArrivals,
    nearbyArrivals,
    arrivalsTimestamp,
    refreshArrivals,
  } = useNearbyOrFavoriteArrivals(true);

  const { currentPosition, startPositionWatch, geolocationApproved } =
    useGeolocation();

  const geolocationLoading =
    currentPosition.fetching && !currentPosition.error && !nearbyArrivals;

  const { currentLocation, manualLocationClear } = useCurrentLocation();
  const { transitTrackerViewOpened, viewClosed } = useViewMapControls();
  useInterval(refreshArrivals, ARRIVALS_REFRESH_INTERVAL);

  useEffect(() => {
    setHighlightedStop(null);
  }, [setHighlightedStop]);

  const sharePosition = useCallback(() => {
    startPositionWatch();
  }, [startPositionWatch]);

  const clearManualLocation = useCallback(() => {
    manualLocationClear();
  }, [manualLocationClear]);

  useEffect(() => {
    transitTrackerViewOpened();
    return viewClosed;
  }, [transitTrackerViewOpened, viewClosed]);

  useEffect(() => {
    if (currentLocation || favoriteStops.length) {
      refreshArrivals();
    }
  }, [currentLocation, favoriteStops, refreshArrivals]);

  // Using a placeholder input to hold cursor focus is meant to tackle an iOS
  // limitation: Currently, the only place one can set the focus is in the
  // handler of a user click. This way, we can set the omnibox input to
  // receive focus when the user clicks the link to the search view
  const safariFocusHolderInputRef = useRef();
  const focusSafariInput = useCallback(() => {
    safariFocusHolderInputRef.current.focus();
  }, []);

  const navigateToOmnibox = () => {
    focusSafariInput();
    navigate(ROUTES.search);
  };

  const navigateToPlanner = useCallback(
    () => navigate(ROUTES.planner),
    [navigate]
  );

  const dispatch = useDispatch();
  const zoomToLocation = () => {
    dispatch(setZoomToLocation(currentLocation));
  };

  const { setMultiCurrentQueryParams } = useCurrentQuery();

  useEffect(() => {
    // restore default trip planner settings when this component mounts
    setMultiCurrentQueryParams(getDefaultQuery());
  }, [setMultiCurrentQueryParams]);

  const tabConfig = useMemo(
    () =>
      [
        {
          id: 'tracker',
          route: ROUTES.home,
          dataGtm: GTM_HANDLES.home.trackerTab,
        },
        {
          id: 'planner',
          route: ROUTES.planner,
          dataGtm: GTM_HANDLES.home.plannerTab,
        },
        {
          id: 'routes',
          route: ROUTES.routes,
          dataGtm: GTM_HANDLES.home.routesTab,
        },
        {
          id: 'alerts',
          route: ROUTES.alerts,
          dataGtm: GTM_HANDLES.home.alertsTab,
        },
      ].map(t => ({
        ...t,
        active: matchPath([t.route]),
      })),
    [matchPath]
  );

  const isHome = matchPath([ROUTES.home]);
  const containerGtmHandle = GTM_HANDLES.home.trackerNearbyStops;
  const isManualLocation = useSelector(s => s.shared.location.isManualLocation);
  const isArrivalsError = useSelector(s => s.transitTracker.isArrivalsError);
  const mobileMode = useSelector(s => s.user.mobileMode);
  const { outOfBounds } = useSelector(s => s.shared.location.currentPosition);
  const showFavoriteArrivals = favoriteStops?.length > 0;
  const showNearbyArrivals = !!currentLocation;
  const showArrivalsHeader = showFavoriteArrivals || showNearbyArrivals;
  const currentLocationName = currentLocation ? currentLocation.name : null;

  const [mountMinimizeToggle, setMountMinimizeToggle] = useState(false);
  const [showMinimizeToggle, setShowMinimizeToggle] = useState(false);

  const makeMinimizeToggleVisible = useCallback(() => {
    setMountMinimizeToggle(true);
    setShowMinimizeToggle(true);
  }, []);

  useEffect(() => {
    // this prevents the toggle from appearing momentarily if the user
    // switches from mobile to desktop mode by e.g. rotating their device
    if (mobileMode) setMountMinimizeToggle(false);
  }, [mobileMode]);

  const hoverLeaveTimeoutRef = useRef();

  const setHideMinimizeToggleTimeout = useCallback(() => {
    clearTimeout(hoverLeaveTimeoutRef?.current);

    hoverLeaveTimeoutRef.current = setTimeout(() => {
      setShowMinimizeToggle(false);
      hoverLeaveTimeoutRef.current = null;
    }, 2000);
  }, []);

  useEffect(() => () => clearTimeout(hoverLeaveTimeoutRef.current), []);

  const { mapDefaultScrollMobile } = useSelector(s => s.map);

  // counteract scroll to (0, 0) that is applied by some browsers
  // (e.g. Chrome) when the root view/route is revisited
  useEffect(() => {
    if (mobileMode) window.scrollTo(0, mapDefaultScrollMobile);
  }, [mobileMode, mapDefaultScrollMobile]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (outOfBounds || seekingGeoUpdate) {
        setSeekingGeoUpdate(true);
        startPositionWatch(true);
      } else {
        setSeekingGeoUpdate(false);
      }
    }, GEOLOCATION_REPOLL_INTERVAL);

    return () => {
      clearInterval(intervalId); // Clear the interval when the component unmounts.
    };
  }, [outOfBounds, seekingGeoUpdate, startPositionWatch]);

  const showCurrentLocationPrompt =
    !currentLocation &&
    (!geolocationApproved || currentPosition.error || seekingGeoUpdate);

  const showChevron = mountMinimizeToggle && showMinimizeToggle;
  const currentLocOrFavorites = !!(
    !showCurrentLocationPrompt || favoriteStops.length
  );

  return (
    <>
      <HistoryManager />
      <SafariFocusHolderInput ref={safariFocusHolderInputRef} />
      <Styled.HeaderAndToolsWrapper
        alertBannerIsVisible={alertBannerIsVisible}
        onMouseMove={makeMinimizeToggleVisible}
        onMouseEnter={makeMinimizeToggleVisible}
        onMouseLeave={setHideMinimizeToggleTimeout}
        onTouchEnd={setHideMinimizeToggleTimeout}
      >
        {!sidePanelIsMinimized ? (
          <>
            {!mobileMode && (
              <Styled.MinMaxSidePanelButtonWrapper
                showMinimizeToggle={showMinimizeToggle}
                mountMinimizeToggle={mountMinimizeToggle}
              >
                <Styled.MinMaxSidePanelButton
                  showMinimizeToggle={showMinimizeToggle}
                  minimized={sidePanelIsMinimized}
                  onClick={() => toggleSidePanelMinMax()}
                  aria-label={intl.formatMessage({
                    id: 'detailview.sidepanel_label',
                  })}
                  aria-expanded={!sidePanelIsMinimized}
                  aria-controls="side-panel"
                  title={intl.formatMessage({
                    id: sidePanelIsMinimized
                      ? 'detailview.sidepanel_expand'
                      : 'detailview.sidepanel_minimize',
                  })}
                >
                  <Styled.SidePanelChevronIcon
                    $showChevron={showChevron}
                    $sidePanelIsMinimized={sidePanelIsMinimized}
                    aria-hidden="true"
                  />
                </Styled.MinMaxSidePanelButton>
              </Styled.MinMaxSidePanelButtonWrapper>
            )}

            <TripToolTabs
              tabConfig={tabConfig}
              sidePanelIsMinimized={sidePanelIsMinimized}
            />
          </>
        ) : (
          <DetailNavHeader isHome={isHome} />
        )}
        <Styled.TripToolWrapper minimized={readyToRemoveFromDom}>
          {!isArrivalsError && (
            <Styled.StopsWrapper>
              {showArrivalsHeader && (
                <>
                  <Styled.ArrivalsHeader>
                    <OmniboxButton
                      currentLocOrFavorites={currentLocOrFavorites}
                      navigateToOmnibox={navigateToOmnibox}
                    />
                    <Styled.TimestampRefreshButton
                      timestamp={arrivalsTimestamp}
                      onClick={refreshArrivals}
                    />
                  </Styled.ArrivalsHeader>
                  {showFavoriteArrivals && (
                    <>
                      <Styled.FavoritesContainer>
                        <Styled.FavoriteStar aria-hidden="true" role="img" />
                        <Styled.HeaderText>
                          <FormattedMessage id="omnibox.favorites" />
                        </Styled.HeaderText>
                      </Styled.FavoritesContainer>
                      <StopsList
                        arrivals={favoriteArrivals || []}
                        toggleFavoriteStop={toggleFavoriteStop}
                        isFavoriteStop={isFavoriteRouteStop}
                        navigateToStopAndSign={navigateToStopAndSign}
                        navigateToPlanner={navigateToPlanner}
                        highlightStop={setHighlightedStop}
                        dataGtm={containerGtmHandle}
                        componentMode="favorites"
                      />
                    </>
                  )}
                </>
              )}
              {showNearbyArrivals && (
                <>
                  <Styled.CurrentLocationHeaderWrapper>
                    <Styled.CurrentLocationButton onClick={zoomToLocation}>
                      <Styled.CurrenLocationMarkerWrapper>
                        <Styled.CurrentLocationMarker
                          pulsate={!isManualLocation}
                        />
                      </Styled.CurrenLocationMarkerWrapper>
                      <Styled.HeaderText>
                        <FormattedMessage id="nearbystops.header" />{' '}
                        {currentLocationName}
                      </Styled.HeaderText>
                    </Styled.CurrentLocationButton>
                    {isManualLocation && (
                      <Styled.ClearCurrentLocationButton
                        onClick={clearManualLocation}
                        title={intl.formatMessage({ id: 'nearbystops.clear' })}
                      >
                        <Styled.ClearCurrentLocationIcon />
                      </Styled.ClearCurrentLocationButton>
                    )}
                  </Styled.CurrentLocationHeaderWrapper>
                  <StopsList
                    arrivals={nearbyArrivals || []}
                    toggleFavoriteStop={toggleFavoriteStop}
                    isFavoriteStop={isFavoriteRouteStop}
                    navigateToStopAndSign={navigateToStopAndSign}
                    navigateToPlanner={navigateToPlanner}
                    highlightStop={setHighlightedStop}
                    dataGtm={containerGtmHandle}
                    componentMode="nearbyUser"
                    selectedLocation={currentLocation}
                  />
                </>
              )}
              {showCurrentLocationPrompt && (
                <CurrentLocationPrompt
                  isLocationSet={currentLocation}
                  sharePosition={sharePosition}
                  isGeolocationLoading={geolocationLoading}
                  hasGeolocationError={!!currentPosition.error}
                  isGeolocationOutOfBounds={currentPosition.outOfBounds}
                  userHasFavoriteStops={!!favoriteStops.length}
                  navigateToOmnibox={navigateToOmnibox}
                  navigateToPlanner={navigateToPlanner}
                  attemptingToRefreshGeolocation={seekingGeoUpdate}
                  currentLocOrFavorites={currentLocOrFavorites}
                />
              )}
              {(!currentLocation ||
                nearbyArrivals ||
                !!favoriteStops.length) && (
                <BrandFooter
                  inverse={!!currentLocation || !!favoriteStops.length}
                />
              )}
            </Styled.StopsWrapper>
          )}
          {isArrivalsError && (
            <ErrorWithCountdown
              refreshInterval={ARRIVALS_REFRESH_INTERVAL}
              errorMessageId="arrivals.error"
            />
          )}
        </Styled.TripToolWrapper>
      </Styled.HeaderAndToolsWrapper>
    </>
  );
}

export default TripToolsContainer;
